// Shared constants

// API Constants
export const API_VERSION = 'v1';
export const API_PREFIX = `/api/${API_VERSION}`;

// JWT Constants
export const JWT_DEFAULT_EXPIRES_IN = '7d';
export const JWT_REFRESH_DEFAULT_EXPIRES_IN = '30d';
export const JWT_MIN_SECRET_LENGTH = 32;

// Pagination Constants
export const DEFAULT_PAGE = 1;
export const DEFAULT_LIMIT = 10;
export const MAX_LIMIT = 100;

// Customer Numbering
export const CUSTOMER_NUMBER_PREFIX = 'CUST';
export const CUSTOMER_NUMBER_PADDING = 6;

// Invoice Constants
export const INVOICE_NUMBER_PREFIX = 'INV';
export const INVOICE_NUMBER_PADDING = 6;

// Tax Constants (Indonesia)
export const TAX_RATES = {
  PPN: 11, // PPN rate in Indonesia
  PPH_23: 2, // PPh 23 rate for services
  PPH_21: 2, // PPh 21 rate for individuals
} as const;

// Payment Constants
export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'check',
  'credit_card',
  'debit_card',
  'digital_wallet',
  'pph_withholding',
  'other',
] as const;

export const PAYMENT_STATUSES = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'refunded',
] as const;

// Invoice Statuses
export const INVOICE_STATUSES = ['draft', 'published', 'overdue', 'voided', 'cancelled'] as const;

// Customer Statuses
export const CUSTOMER_STATUSES = ['active', 'inactive', 'suspended'] as const;

// Customer Types
export const CUSTOMER_TYPES = ['individual', 'company', 'government', 'non_profit'] as const;

// Communication Channels
export const COMMUNICATION_CHANNELS = ['email', 'whatsapp', 'sms', 'phone'] as const;

// File Upload Constants
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

// Rate Limiting
export const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute
export const RATE_LIMIT_MAX_REQUESTS = 100;

// Date Formats
export const DATE_FORMATS = {
  ISO: 'YYYY-MM-DD',
  LOCALE: 'DD MMMM YYYY',
  SHORT: 'DD MMM YYYY',
  DATE_TIME: 'DD MMM YYYY HH:mm',
} as const;

// Currency Codes
export const CURRENCY_CODES = [
  'IDR', // Indonesian Rupiah
  'USD', // US Dollar
  'EUR', // Euro
  'SGD', // Singapore Dollar
  'MYR', // Malaysian Ringgit
] as const;

// Currencies with no fractional units (0 decimal places)
export const ZERO_DECIMAL_CURRENCIES = ['IDR'] as const;

// Regex Patterns
export const PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[+]?[1-9]\d{1,14}$/,
  URL: /^https?:\/\/.+/,
  OBJECT_ID: /^[0-9a-fA-F]{24}$/,
  TAX_ID_NPWP: /^\d{2}\.\d{3}\.\d{3}\.\d{1}-\d{3}\.\d{3}$/,
  TAX_ID_15_DIGIT: /^\d{15}$/,
  CUSTOMER_NUMBER: /^[A-Z0-9]{3}-CUST-\d{6}$/,
  INVOICE_NUMBER: /^[A-Z0-9]{3}-\d{8}$/,
} as const;

// Error Codes
export const ERROR_CODES = {
  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',

  // Business logic errors
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Internal errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const;

// Expense Statuses
export const EXPENSE_STATUSES = ['draft', 'pending', 'paid', 'voided', 'cancelled'] as const;

// Expense Frequencies
export const EXPENSE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const;

// Default Expense Categories (seeded per tenant)
export const DEFAULT_EXPENSE_CATEGORIES = [
  'Office Supplies',
  'Travel',
  'Utilities',
  'Rent',
  'Insurance',
  'Professional Services',
  'Software & Subscriptions',
  'Marketing',
  'Meals & Entertainment',
  'Equipment',
  'Miscellaneous',
] as const;

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
} as const;
